import { type TTrainingOfferCreation, formatName } from "@api";
import type { ITrainingOffer } from "@bbj-types/offer";
import { TrainingOfferModality } from "@root/utils/enums/offer";
import { formatInputPhoneNumber } from "@root/utils/phoneLib";
import type { DefaultValues } from "react-hook-form";
import type { TTrainingSharedFormValues } from "./types";

type TModalitiesForm = Pick<TTrainingSharedFormValues, "wage" | "validation" | "remote">;

const formatModalitiesFormToOffer = (params: TModalitiesForm) => {
  return Object.keys(params).reduce<TrainingOfferModality[]>((acc, key) => {
    if (params[key] && Object.values(TrainingOfferModality).includes(params[key])) {
      acc.push(params[key]);
    }

    return acc;
  }, []);
};

const formatModalitiesOfferToForm = (modalities: TrainingOfferModality[]): TModalitiesForm => {
  const modalitiesFormName: TModalitiesForm = { validation: "", wage: "", remote: null };

  for (const modality of modalities) {
    switch (modality) {
      case TrainingOfferModality.CERTIFYING:
      case TrainingOfferModality.QUALIFYING:
        modalitiesFormName.validation = modality;
        break;
      case TrainingOfferModality.PAID:
      case TrainingOfferModality.UNPAID:
        modalitiesFormName.wage = modality;
        break;
      case TrainingOfferModality.FULL_REMOTE:
        modalitiesFormName.remote = modality;
        break;
    }
  }

  return modalitiesFormName;
};

export const formatTrainingSharedFormToOffer = (data: TTrainingSharedFormValues): TTrainingOfferCreation => {
  const {
    structureName,
    structureWebsite,
    validation,
    wage,
    remote,
    period,
    address,
    description,
    beneficiaries,
    financingModes,
    trainingModalities,
    registrationModalities,
    ...rest
  } = data;

  const modalities = formatModalitiesFormToOffer({ validation, wage, remote });

  return {
    ...rest,
    trainingStartDate: period?.startDate ?? undefined,
    trainingEndDate: period?.endDate ?? undefined,
    modalities,
    address: address ?? undefined,
    description: description?.html ?? "",
    beneficiaries: beneficiaries?.html ?? "",
    financingModes: financingModes?.html ?? "",
    trainingModalities: trainingModalities?.html ?? "",
    registrationModalities: registrationModalities?.html ?? "",
    provider: {
      name: structureName,
      website: structureWebsite,
    },
    contact: data.contact
      ? {
          name: formatName(data.contact),
          mail: data.contact.email ?? "",
          phone: data.contact.phone ?? "",
        }
      : undefined,
  };
};

export const formatTrainingOfferToSharedForm = (
  trainingOffer: Partial<ITrainingOffer>
): DefaultValues<TTrainingSharedFormValues> => {
  const {
    offerId,
    provider,
    trainingStartDate,
    trainingEndDate,
    modalities = [],
    description,
    beneficiaries,
    financingModes,
    trainingModalities,
    registrationModalities,
    contact,
    address,
    url,
  } = trainingOffer;
  const period = offerId
    ? {
        startDate: new Date(trainingStartDate || 0),
        endDate: new Date(trainingEndDate || 0),
      }
    : {
        startDate: null,
        endDate: null,
      };
  const { wage, validation, remote } = formatModalitiesOfferToForm(modalities);

  return {
    period: period.endDate || period.startDate ? period : null,
    wage: wage ?? "",
    validation: validation ?? "",
    remote: remote ?? null,
    description: { html: description ?? "", text: "" },
    beneficiaries: { html: beneficiaries ?? "", text: "" },
    financingModes: { html: financingModes ?? "", text: "" },
    trainingModalities: { html: trainingModalities ?? "", text: "" },
    registrationModalities: { html: registrationModalities ?? "", text: "" },
    address: address ?? null,
    url: url ?? "",
    name: trainingOffer.name ?? "",
    structureName: provider?.name ?? "",
    structureWebsite: provider?.website ?? "",
    contact: contact
      ? {
          email: contact.mail ?? "",
          firstName: contact.name?.split(" ")[0] ?? "",
          lastName: contact.name?.substring(contact.name.indexOf(" ") + 1) ?? "",
          phone: formatInputPhoneNumber(contact.phone) ?? "",
        }
      : null,
  };
};
